import { useEffect, useState } from 'react';
import { CheckCircle2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import FlowShell from './FlowShell';
import {
  CARD_STYLE, TextField, SelectField, TextareaField, RadioField, CheckboxField,
  NextButton, PrimaryButton, formatServicePrice,
} from './FlowField';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const STEP_LABELS = ['Your Business', 'Digital Presence', 'Your Goals', 'Recommendation'];

const BUSINESS_TYPE = [
  { value: 'b2b',  label: 'B2B — I sell to other businesses' },
  { value: 'b2c',  label: 'B2C — I sell to consumers' },
  { value: 'both', label: 'Both' },
];
const INDUSTRY_OPTIONS = [
  'Technology','Professional Services','Retail','Hospitality','Healthcare',
  'Education','Finance','Construction','Creative','Other',
];
const REVENUE_OPTIONS = [
  { value: 'under_5k',   label: 'Under £5k' },
  { value: '5k_20k',     label: '£5k to £20k' },
  { value: '20k_50k',    label: '£20k to £50k' },
  { value: '50k_200k',   label: '£50k to £200k' },
  { value: '200k_plus',  label: '£200k plus' },
];

const PAID_OPTIONS = [
  { value: 'working',   label: 'Yes working well' },
  { value: 'poor',      label: 'Yes but poor results' },
  { value: 'stopped',   label: 'Tried and stopped' },
  { value: 'never',     label: 'Never tried' },
];
const SEO_OPTIONS = [
  { value: 'none',       label: 'Not appearing on Google at all' },
  { value: 'brand_only', label: 'Visible for brand name only' },
  { value: 'some',       label: 'Ranking for some keywords' },
  { value: 'strong',     label: 'Strong organic traffic' },
];
const EMAIL_OPTIONS = [
  { value: 'none',       label: 'No email list' },
  { value: 'rare',       label: 'Have a list but rarely use it' },
  { value: 'regular',    label: 'Send emails regularly' },
];
const SOCIAL_OPTIONS = [
  { value: 'none',        label: 'No presence' },
  { value: 'rare',        label: 'Profiles but rarely post' },
  { value: 'low_engage',  label: 'Post regularly with little engagement' },
  { value: 'strong',      label: 'Strong and active' },
];

const SUCCESS_OPTIONS = [
  { value: 'traffic',   label: 'More website traffic' },
  { value: 'leads',     label: 'More qualified leads' },
  { value: 'conv',      label: 'Higher conversion rate' },
  { value: 'brand',     label: 'Better brand awareness' },
  { value: 'existing',  label: 'More revenue from existing customers' },
  { value: 'market',    label: 'Own a specific keyword or market' },
];
const BUDGET_OPTIONS = [
  { value: 'under_500',  label: 'Under £500' },
  { value: '500_1k',     label: '£500 to £1k' },
  { value: '1k_3k',      label: '£1k to £3k' },
  { value: '3k_5k',      label: '£3k to £5k' },
  { value: '5k_plus',    label: '£5k plus' },
];

function buildRecommendation(form, pricing) {
  const by = {};
  for (const r of pricing) by[r.id] = r;
  const starter     = formatServicePrice(by['reach-visibility-starter']);
  const accelerator = formatServicePrice(by['reach-growth-accelerator']);
  const retainer    = formatServicePrice(by['reach-full-reach-retainer']);

  const rev = form.monthly_revenue;
  const seo = form.seo_situation;
  const paid = form.paid_advertising;
  const budget = form.monthly_marketing_budget;
  const goals = form.success_in_6m || [];

  let title = 'Recommended next step';
  let body = '';
  let cta = '';

  if (rev === 'under_5k' && seo === 'none' && budget === 'under_500') {
    title = 'Build your organic foundation first.';
    body = 'At your current stage the highest return is building your organic presence on Google. Paid advertising requires a budget you cannot yet afford to run at the scale needed for real results — organic SEO compounds over time and builds an asset you own permanently. We will build your foundation correctly from day one so every future investment builds on solid ground.';
    cta = `Visibility Starter starting from ${starter}.`;
  } else if (rev === '20k_50k' && paid === 'poor' && goals.includes('leads')) {
    title = 'Rebuild your acquisition funnel.';
    body = 'Poor paid ad results are almost never a budget problem — they are a targeting and messaging problem. You are probably reaching the wrong people with the wrong message at the wrong moment. We will audit your current setup, fix the fundamentals, and rebuild your acquisition funnel properly.';
    cta = `Growth Accelerator starting from ${accelerator}.`;
  } else if (rev === '50k_200k' && (seo === 'some' || seo === 'brand_only') && goals.includes('market')) {
    title = 'Move to a coordinated multi-channel strategy.';
    body = 'You have a working business and some digital presence. This is the stage where the right investment in a coordinated strategy creates a step change in results. SEO, content, paid media, and email working together will put you in front of your ideal customers at every stage of their decision journey.';
    cta = `Full Reach Retainer starting from ${retainer}.`;
  } else if (rev === '200k_plus' && seo === 'strong' && goals.includes('brand')) {
    title = 'Differentiate on authority and positioning.';
    body = 'At your revenue level the differentiator is authority and positioning — being the name that comes up in every relevant conversation, the business that prospects already know before they need you. We will build that positioning deliberately and systematically. Retainers at this level are scoped to your specific objectives.';
    cta = 'Let us talk about what that looks like for your business.';
  } else {
    // Fallback — general recommendation based on revenue tier
    if (rev === 'under_5k' || rev === '5k_20k') {
      title = 'Start with visibility — compound the easy wins.';
      body = 'Based on your answers, the right first move is to build your organic visibility so your ideal customers can actually find you. Small investments here compound quickly and set the foundation for everything else.';
      cta = `Visibility Starter starting from ${starter}.`;
    } else {
      title = 'Run paid + organic in parallel.';
      body = 'You have enough revenue momentum to benefit from a coordinated organic + paid strategy. We will prioritise the highest-ROI channels for your business, set up proper tracking, and build out from there.';
      cta = `Growth Accelerator starting from ${accelerator}.`;
    }
  }

  // No-email-list addendum — always append if they have no list
  const addendum = form.email_list === 'none'
    ? 'One quick win regardless of your other priorities: building an owned email list costs almost nothing and gives you a direct line to your customers that no algorithm controls. We will include an email list growth strategy in your recommendation.'
    : null;

  return { title, body, cta, addendum };
}

export default function ReachFlow({ onExit }) {
  const [step, setStep] = useState(1);
  const [pricing, setPricing] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    first_name: '', last_name: '', business_name: '', business_website: '',
    email: '', business_type: '', industry: '', monthly_revenue: '',
    paid_advertising: '', seo_situation: '', email_list: '', social_media: '',
    success_in_6m: [], monthly_marketing_budget: '', additional_details: '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/service-pricing`);
        if (res.ok && !cancelled) setPricing((await res.json()).rows || []);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const step1Ready = form.first_name && form.last_name && form.business_name
    && emailValid && form.business_type && form.industry && form.monthly_revenue;
  const step2Ready = form.paid_advertising && form.seo_situation && form.email_list && form.social_media;
  const step3Ready = form.success_in_6m.length > 0 && form.monthly_marketing_budget;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email.trim().toLowerCase(),
          service: 'reach',
          source: 'pricing_questionnaire',
          interested_in: `Acodia Reach · ${form.business_name}`,
          answers: { ...form, email: undefined },
        }),
      });
      if (!res.ok) throw new Error(`submit failed: ${res.status}`);
      setSubmitted(true);
    } catch (err) {
      toast.error('Submission failed', { description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const rec = buildRecommendation(form, pricing);

  const renderStep1 = () => (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="First Name" value={form.first_name} onChange={(v) => set('first_name', v)} required testId="reach-first" />
        <TextField label="Last Name" value={form.last_name} onChange={(v) => set('last_name', v)} required testId="reach-last" />
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField label="Business Name" value={form.business_name} onChange={(v) => set('business_name', v)} required testId="reach-business-name" />
        <TextField label="Website" value={form.business_website} onChange={(v) => set('business_website', v)} placeholder="optional" testId="reach-website" />
      </div>
      <div className="mt-4">
        <TextField label="Email" type="email" value={form.email} onChange={(v) => set('email', v)} required testId="reach-email" />
        {form.email && !emailValid && <p className="font-body text-[11px] mt-1" style={{ color: 'var(--warn)' }}>Please enter a valid email address.</p>}
      </div>
      <div className="mt-4">
        <RadioField label="Business type" value={form.business_type} onChange={(v) => set('business_type', v)} options={BUSINESS_TYPE} required testId="reach-business-type" />
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SelectField label="Industry" value={form.industry} onChange={(v) => set('industry', v)} options={INDUSTRY_OPTIONS} required testId="reach-industry" />
        <SelectField label="Monthly revenue" value={form.monthly_revenue} onChange={(v) => set('monthly_revenue', v)} options={REVENUE_OPTIONS} required testId="reach-revenue" />
      </div>
      <NextButton disabled={!step1Ready} onClick={() => setStep(2)} testId="reach-step-1-next" />
    </>
  );

  const renderStep2 = () => (
    <>
      <RadioField label="Paid advertising currently" value={form.paid_advertising} onChange={(v) => set('paid_advertising', v)} options={PAID_OPTIONS} required testId="reach-paid" />
      <div className="mt-4">
        <RadioField label="Current SEO situation" value={form.seo_situation} onChange={(v) => set('seo_situation', v)} options={SEO_OPTIONS} required testId="reach-seo" />
      </div>
      <div className="mt-4">
        <RadioField label="Email list" value={form.email_list} onChange={(v) => set('email_list', v)} options={EMAIL_OPTIONS} required testId="reach-email-list" />
      </div>
      <div className="mt-4">
        <RadioField label="Social media" value={form.social_media} onChange={(v) => set('social_media', v)} options={SOCIAL_OPTIONS} required testId="reach-social" />
      </div>
      <NextButton disabled={!step2Ready} onClick={() => setStep(3)} testId="reach-step-2-next" />
    </>
  );

  const renderStep3 = () => (
    <>
      <CheckboxField label="Success in 6 months (select all that apply)" values={form.success_in_6m} onChange={(v) => set('success_in_6m', v)} options={SUCCESS_OPTIONS} required testId="reach-success" />
      <div className="mt-4">
        <RadioField label="Monthly marketing budget" value={form.monthly_marketing_budget} onChange={(v) => set('monthly_marketing_budget', v)} options={BUDGET_OPTIONS} required testId="reach-budget" />
      </div>
      <div className="mt-4">
        <TextareaField
          label="Additional details"
          hint="Tell us about your business, your ideal customers, your biggest marketing challenge, or anything specific you want us to focus on."
          value={form.additional_details}
          onChange={(v) => set('additional_details', v)}
          placeholder="Optional — the more context you share, the more useful your consultation will be."
          rows={5}
          testId="reach-details"
        />
      </div>
      <NextButton disabled={!step3Ready} onClick={() => setStep(4)} testId="reach-step-3-next" />
    </>
  );

  const renderStep4 = () => {
    if (submitted) {
      return (
        <div className="text-center max-w-xl mx-auto py-6" data-testid="reach-success">
          <div
            className="inline-flex items-center justify-center rounded-full mb-6"
            style={{ width: 64, height: 64, background: 'var(--ok-10)', border: '1px solid var(--ok)', boxShadow: '0 0 40px rgba(74,222,128,0.25)' }}
          >
            <CheckCircle2 size={32} style={{ color: 'var(--ok)' }} />
          </div>
          <h2 className="font-display text-2xl font-bold mb-2" style={{ color: 'var(--white)' }}>
            Your details have been received.
          </h2>
          <p className="font-body text-sm" style={{ color: 'var(--tm)' }}>
            Expect a response within one business day. We will be in touch to confirm your growth audit time.
          </p>
        </div>
      );
    }
    return (
      <>
        <div className="p-6 rounded-lg mb-6" style={{ ...CARD_STYLE, background: 'var(--a-glow)', border: '1px solid var(--a-border)' }} data-testid="reach-recommendation">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
            recommendation
          </p>
          <h3 className="font-display text-xl font-semibold mt-2 mb-3" style={{ color: 'var(--white)' }}>{rec.title}</h3>
          <p className="font-body text-sm mb-4" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>{rec.body}</p>
          {rec.addendum && (
            <p
              className="font-body text-sm mb-4 pl-3"
              style={{ color: 'var(--tm)', lineHeight: '1.72', borderLeft: '2px solid var(--a)' }}
              data-testid="reach-email-list-addendum"
            >
              {rec.addendum}
            </p>
          )}
          <p className="font-mono text-sm" style={{ color: 'var(--a)' }}>{rec.cta}</p>
        </div>
        <PrimaryButton onClick={handleSubmit} disabled={submitting} testId="reach-step-4-submit">
          <Mail size={14} className="mr-2" />
          {submitting ? 'Sending…' : 'Book My Free Growth Audit'}
        </PrimaryButton>
      </>
    );
  };

  return (
    <FlowShell
      kicker="acodia reach"
      title={submitted ? 'Thanks — we will be in touch.' : 'Let us build your growth plan.'}
      stepLabels={STEP_LABELS}
      step={step}
      onBack={() => setStep((s) => Math.max(1, s - 1))}
      onExit={onExit}
    >
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
    </FlowShell>
  );
}
